import React, { FC, useContext, useMemo, useState } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Title,
  Box,
  Flex,
  Text,
  Tooltip,
  Menu,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import SyncContext from "../../../../context/SyncContext";
import { DomainHeaderIcon, ProductAreaHeaderIcon } from "../../../../icons/x-symbol-svgrepo-com";
import BagelAutocomplete from "../../../general/BagelAutocomplete";
import { IconArrowLeft, IconX } from "@tabler/icons";
import BagelAccordionMenu from "../../../general/BagelAccordionMenu";

export interface AssignToNewIdeaFormProps {
  evidence?: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  loading: boolean;
}

const AssignToNewIdeaForm: FC<AssignToNewIdeaFormProps> = ({
  evidence,
  onCancel,
  onSubmit,
  loading,
}) => {
  // Form setup
  const form = useForm({
    initialValues: {
      title: evidence?.title || "",
      description: evidence?.text || "",
      domainId: evidence?.domainId || "",
      componentId: evidence?.componentId || "",
    },
  });

  // Context
  const { allComponents, components, domains } = useContext(SyncContext);

  // Find the component (parent or child) with the evidence component id
  const parentComponent = components.find((c: any) => c._id === evidence?.componentId);
  const childComponent = components
    .flatMap((parent: any) => parent.childComponents || [])
    .find((child: any) => child._id === evidence?.componentId);
  const matchedComponent = parentComponent || childComponent;

  const component = useMemo(() => {
    if (evidence?.componentObj) {
      return allComponents.find((comp) => comp._id === evidence?.componentObj?._id);
    }
    return null;
  }, [evidence?.componentObj, allComponents]);

  const mlComponent = useMemo(() => {
    if (evidence?.ml?.mlComponentId) {
      return allComponents.find((comp) => comp._id === evidence.ml.mlComponentId);
    }
    return null;
  }, [evidence?.ml?.mlComponentId, allComponents]);

  // Is this an AI suggestion that hasn't been accepted yet?
  const isAiSuggestion = evidence?.ml?.mlComponentId && !evidence?.componentObj?._id;
  


  const [domain, setDomain] = useState<string | null>(matchedComponent?.domain || null);
  const [componentName, setComponentName] = useState<string | null>(matchedComponent?.name || null);
  const [productArea, setProductArea] = useState<any>(component || (isAiSuggestion ? mlComponent : null));

  // Example flag that you might use
  const isGenerated = false;
  // Dummy displayState to mimic conditional rendering (adjust as needed)
  const displayState = { domain: true, component: true };

  // Handler to update the form when a domain is selected
  const handleDomainSelect = (selectedDomain: string) => {
    setDomain(selectedDomain);
    // Find the corresponding domain _id
    const domainObject = domains.find((d: any) => d.name === selectedDomain);
    if (domainObject) {
      // Update the form value for domainId
      form.setFieldValue("domainId", domainObject._id);
    }
    // Reset product area selection when domain changes
    setComponentName(null);
    setProductArea(null);
    form.setFieldValue("componentId", "");
  };

  const handleProductAreaSelect = async (item: any) => {
    setComponentName(item.name);
    setProductArea(item);
    form.setFieldValue("componentId", item._id || "");
  };

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        if (!loading) onSubmit(values);
      })}
    >
      <Group position="apart">
        <Title order={5} mb="xs">
        Assign to a new idea
        </Title>
        <Button size="xs" variant="outline" radius="md" color="gray" onClick={onCancel} leftIcon={<IconX size={16} />}>
            Cancel
        </Button>
      </Group>
      <Stack spacing={10}>
        <TextInput
          label="Title"
          radius="md"
          placeholder="Idea title"
          withAsterisk
          {...form.getInputProps("title")}
        />
        <Textarea
          label="Description"
          radius="md"
          placeholder="Idea description"
          withAsterisk
          minRows={2}
          autosize
          {...form.getInputProps("description")}
        />
        <Group>
          {/* Product Area Component */}
          {displayState?.component && (
            <Box>
              <Flex ml="4px" align="center" mb="4px">
                <ProductAreaHeaderIcon />
                <Text fz="12px" color="#585C68" ml="6px" sx={{ lineHeight: "16px" }}>
                Product Area
                </Text>
              </Flex>
              <BagelAccordionMenu
                selectedComponent={productArea}

                isAiSuggestion={false}
                onSelect={(item: any, type: string) => {
                  if (type === 'Domain') {
                    // Handle domain selection if needed
                    setDomain(item.name);
                    // This is handled by the useMemo already
                  } else {
                    // Handle product area selection
                    
                    handleProductAreaSelect(item);
                  }
                }}
              >
                <Flex
                  h={24}
                  px={8}
                  py={4}
                  mx="2px"
                  bg="#5C5CEB1A"
                  justify="space-evenly"
                  align="center"
                  sx={{
                    borderRadius: "4px",
                    border: "0.8px solid #ced4da",
                    cursor: isGenerated ? "default" : "pointer",
                  }}
                >
                  <Text
                    fz="12px"
                    lineClamp={1}
                    tt="capitalize"
                    miw="85px"
                  >
                    {componentName && componentName !== "Unassigned" ? 
                      <Text span color="dimmed">.../ </Text> 
                      : null}
                    {productArea?.name || "Unassigned"}
                  </Text>
                </Flex>
              </BagelAccordionMenu>
            </Box>
          )}
        </Group>

        {/* Form action buttons */}
        <Group
          sx={{
            position: "sticky",
            bottom: 0,
            background: "#F5F6FE",
            paddingBottom: "1rem",
          }}
          mt="md"
          noWrap
        >
          <Button
            fullWidth
            type="submit"
            radius="md"
            loading={loading}
            styles={(theme) => ({
              root: {
                backgroundColor: "#EAEAFE",
                border: 0,
                height: 35,
                "&:hover": {
                  backgroundColor: theme.fn.darken("#EAEAFE", 0.05),
                },
              },
              leftIcon: { marginRight: 15 },
            })}
            sx={{
              border: "1px solid #5C5CEB",
              color: "#5C5CEB",
            }}
          >
            Create Idea with This Evidence
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default AssignToNewIdeaForm;